import {
  IAddress,
  IPriceDisputingPerItem,
  ISystemDetailsPerItem,
  ISystemDetails,
  IMaterialMasterNumbersPerItem,
  IPointOfContact,
} from './common.models';
import { EAddressType } from '../configurations/common';
import { IApproverData } from './customer.models';

export interface IConsentCheckbox {
  name: string,
  label: string,
  isChecked: boolean,
  isRequired: boolean
}

export interface ICheckoutInput {
  type: string;
  id?: string;
  attributes: {
    orderReference?: string;
    redirectUrl?: string | null;
    isExternalRedirect?: string;
    idCart?: string;
    isAddressSavingSkipped?: boolean;
    taxNumber?: string;
    stateDisplayName?: string;
    pointOfContact?: IPointOfContact;
    approverDetails?: IapproverDetails;
    customer: ICustomerCheckout,
    billingAddress?: IAddress;
    shippingAddress?: IAddress;
    soldToAddress?: IAddress;
    payerAddress?: IAddress;
    payments: IPaymentsData[];
    shipment: {
      idShipmentMethod: number;
    },
    termsAccessTime: Date,
    consentedTerms: string[];
    invoiceEmailCopy?: string;
    sapPoNumber?: string;
    orderEmailCopy?: string;
  };
}

export interface ICustomerCheckout {
  attentionTo?: string;
  email: string;
  salutation: string;
  firstName: string;
  idCustomer?: number;
  customerReference: string;
  idCompanyBusinessUnit?: string;
  uuidCompanyUser: string;
  lastName: string;
}

export interface ICheckout {
  type: string;
  id?: string;
  attributes: {
    orderReference?: string;
    isSap?: boolean;
    redirectUrl?: string | null;
    isExternalRedirect?: string;
    idCart?: string;
    isAddressSavingSkipped?: boolean;
    taxNumber?: string;
    stateDisplayName?: string;
    pointOfContact?: IPointOfContact;
    approverDetails?: IApproverData;
    customer: ICustomerCheckout;
    billingAddress?: IAddress;
    shippingAddress: IAddress;
    soldToAddress?: IAddress;
    payerAddress?: IAddress;
    payments: IPaymentsData[];
    shipment: {
      idShipmentMethod: number;
    }
    termsAccessTime: Date;
    consentedTerms: string[];
    invoiceEmailCopy?: string;
    sapPoNumber?: string;
  };
  links?: {
    self: string;
  };
}

export interface ICheckoutFormData {
  discount: string;
  orderEmailCopy: string;
  purchaseOrderNumber: string;
  taxNumber: string;
  paymentMethod: string;
  vatIdTaxNumber?: string;
}

export interface IAddressData {
  id?: string;
  name: string;
  value: IAddress;
  type?: EAddressType;
  isAddressValid?: boolean;
}

export interface ICheckoutData {
  data: {
    type: string;
    attributes: {
      idCart: string;
      shipment?: {
        idShipmentMethod: number;
      }
      payments?: ICheckoutPaymentsData[];
    }
  };
}

export interface ICheckoutUpdate {
  data: {
    type: string;
    attributes: {
      idCart: string;
      isAddressSavingSkipped?: boolean;
      systemDetails?: ISystemDetails;
      systemDetailsPerItem?: ISystemDetailsPerItem[];
      priceDisputingPerItem?: IPriceDisputingPerItem[];
      pointOfContact?: IPointOfContact;
      approverDetails?: IApproverData;
      materialMasterNumbers?: IMaterialMasterNumbersPerItem[];
      customer?: {
        email?: string;
        salutation?: string;
        firstName?: string;
        idCustomer?: number;
        customerReference?: string;
        uuidCompanyUser?: string;
        lastName?: string;
      },
      billingAddress?: {
        salutation: string;
        firstName: string;
        lastName: string;
        address1: string;
        address2: string;
        address3?: string;
        zipCode: string;
        city: string;
        iso2Code: string;
        company: string;
        phone: string;
        state?: string;
        isDefaultBilling: boolean;
        isDefaultShipping: boolean;
      } | null,
      shippingAddress?: {
        salutation: string;
        firstName: string;
        lastName: string;
        address1: string;
        address2: string;
        address3?: string;
        zipCode: string;
        city: string;
        country: string;
        iso2Code: string;
        company: string | null;
        phone: string | null;
        state?: string;
        isDefaultBilling: boolean;
        isDefaultShipping: boolean;
      },
      soldToAddress?: {
        salutation: string;
        firstName: string;
        lastName: string;
        address1: string;
        address2: string;
        address3?: string;
        zipCode: string;
        city: string;
        iso2Code: string;
        company: string;
        phone: string;
        state?: string;
        isDefaultBilling: boolean;
        isDefaultShipping: boolean;
      } | null,
      payerAddress?: {
        salutation: string;
        firstName: string;
        lastName: string;
        address1: string;
        address2: string;
        address3?: string;
        zipCode: string;
        city: string;
        iso2Code: string;
        company: string;
        phone: string;
        state?: string;
        isDefaultBilling: boolean;
        isDefaultShipping: boolean;
      } | null,
      payments?: IPaymentsData[];
      shipment?: {
        idShipmentMethod: number;
      }
      tax_number?: string,
      isPreview?: boolean,
    }
  };
}

export interface IapproverDetails {
  approverId: string;
  firstName?: string;
  lastName?: string;
  comment?: string;
  dueDate?: string;
}

export interface IPaymentsData {
  paymentMethodName: EPaymentMethodName;
  paymentProviderName: EPaymentProviderName;
  paymentSelection?: EPaymentSelection;
}

export interface ICheckoutPaymentsData {
  paymentMethodName: ECheckoutPaymentMethodName;
  paymentProviderName: EPaymentProviderName;
}

export enum EPaymentMethodName {
  INVOICE      = 'Pay by invoice',
  CONTRACT     = 'contract',
  DUMMY        = 'invoice',
  CC_AVENUE    = 'Online Payment CCAvenue',
  SIPAYCE_CARD = 'Online Card Payment Sipayce',
}

export enum ECheckoutPaymentMethodName {
  CC_AVENUE = 'onlinePaymentCcavenue',
}

export enum EPaymentProviderName {
  INVOICE   = 'InvoicePayment',
  CONTRACT  = 'ContractPayment',
  DUMMY     = 'DummyPayment',
  CC_AVENUE = 'CCAvenue',
  SIPAYCE   = 'Sipayce',
}

export enum EDiscountType {
  DISCOUNT_SHIPPING = 'SHIPPING',
}

export enum EPaymentExpensesName {
  TDS = 'tax.tds',
}

export enum EPaymentSelection {
  INVOICE = 'payByInvoicePurchaseOrder'
}

export interface IAddressesDataIncluded {
  attributes: IAddress;
  id: string;
  links: {
    self: string;
  };
  type: string;
}

export interface ICustomAddressData {
  attributes: IAddress;
  id?: string;
}

export interface IShipmentMethod {
  id: number;
  name: string;
  carrierName: string;
  price: number;
  taxRate: number;
  deliveryTime: number;
  currencyIsoCode: string;
  value?: number;
  shipmentMethodKey?: string;
}

export interface ICCPaymentResponse {
  data: [{
    id: number;
    attributes: ICCPaymentData;
    links: {self: string};
    type: string;
  }];
  links: {
    self: string;
  };
}

export interface ICCPaymentData {
  accessCode: string;
  encryptedData: string;
}

export interface ISipayceCardPaymentData {
  data: {
    type: string;
    attributes: {
      orderReference: string;
      customerReference: string;
      transactionToken: string;
      language: string;
    }
  };
}

export interface ISipayceCardPaymentResponse {
  data: {
    id: string;
    type: string;
    attributes: {
      success: boolean;
      uuid: string;
      purchaseId: string;
      returnType: string;
      redirectUrl: string;
      paymentMethod: string;
      errors: ISipayceCardPaymentError[];
    };
    links: {
      self: string;
    };
  };
}

export interface ISipayceCardPaymentError {
  errorMessage: boolean;
  errorCode: string;
  adapterMessage: string;
  adapterCode: string;
}

export interface IUploadedDocument {
  file: File;
  uploadingInProgress: boolean;
  uploadSuccess: boolean;
  uploadError: string | null;
}

export interface IUploadFileResponse {
  data: {
    type: string;
    id: string;
    attributes: {
      uuid: string;
      file_name: string;
      id_quote_request_version: string | null;
      quote_reference: string;
      id_sales_order: string | null;
      file_content: string | null;
    };
  };
}
